import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationPreferences, useSaveNotificationPreferences } from '../../hooks/useNotifications.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import { toast } from '../../lib/toast.js';
import type { NotificationPreference } from '../../hooks/useNotifications.js';

// Notification types grouped by section
const SECTIONS: { key: string; types: string[] }[] = [
  {
    key: 'orders',
    types: ['order_assigned', 'order_overdue', 'order_due_soon', 'order_stage_changed'],
  },
  {
    key: 'shipping',
    types: ['shipping_delivered'],
  },
  {
    key: 'system',
    types: ['import_complete', 'export_ready'],
  },
];

export default function NotificationsSettings() {
  const { t } = useTranslation('notifications');
  const notificationsEnabled = useFeatureFlag('notifications');

  const { data: serverPrefs = [], isLoading } = useNotificationPreferences();
  const savePrefs = useSaveNotificationPreferences();

  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);

  // Initialise local state when server data arrives
  useEffect(() => {
    if (serverPrefs.length > 0) setPrefs(serverPrefs);
  }, [serverPrefs]);

  function setPref(type: string, field: 'inAppEnabled' | 'emailEnabled', value: boolean) {
    setPrefs((prev) =>
      prev.map((p) => p.notificationType === type ? { ...p, [field]: value } : p),
    );
  }

  function getPref(type: string): NotificationPreference {
    return (
      prefs.find((p) => p.notificationType === type) ?? {
        notificationType: type,
        inAppEnabled: true,
        emailEnabled: false,
      }
    );
  }

  async function handleSave() {
    try {
      await savePrefs.mutateAsync(prefs);
      toast.success(t('preferences.saved'));
    } catch {
      toast.error(t('preferences.save_error'));
    }
  }

  if (!notificationsEnabled) {
    return (
      <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
        Notification management is not enabled for your account.
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
          {t('preferences.title')}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {t('preferences.subtitle')}
        </p>
      </div>

      {isLoading ? (
        <div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-line" style={{ height: 44, marginBottom: 8, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      ) : (
        <>
          {SECTIONS.map((section) => (
            <div
              key={section.key}
              style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 'var(--space-4)' }}
            >
              {/* Section header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', gap: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)' }}>
                <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t(`preferences.sections.${section.key}`)}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                  {t('preferences.in_app')}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                  {t('preferences.email')}
                </p>
              </div>

              {/* Rows */}
              {section.types.map((type, idx) => {
                const pref = getPref(type);
                return (
                  <div
                    key={type}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 80px 80px',
                      gap: 'var(--space-4)',
                      alignItems: 'center',
                      padding: 'var(--space-3) var(--space-4)',
                      borderBottom: idx < section.types.length - 1 ? '1px solid var(--color-border)' : 'none',
                    }}
                  >
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                      {t(`types.${type}`)}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={pref.inAppEnabled}
                        onChange={(e) => setPref(type, 'inAppEnabled', e.target.checked)}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-brand)' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={pref.emailEnabled}
                        onChange={(e) => setPref(type, 'emailEnabled', e.target.checked)}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-brand)' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <button
            onClick={() => void handleSave()}
            disabled={savePrefs.isPending}
            style={{
              padding: 'var(--space-2) var(--space-5)',
              background: 'var(--color-brand)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: savePrefs.isPending ? 'default' : 'pointer',
              opacity: savePrefs.isPending ? 0.7 : 1,
            }}
          >
            {savePrefs.isPending ? t('preferences.saving') : t('preferences.save')}
          </button>
        </>
      )}
    </div>
  );
}
